import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';

import { login, register, getCurrentUser, logout } from '../../services/authService';
import { setMemoryAccessToken } from '../../services/authToken';
import { fetchUserPermissions } from '../../services/permissionsService';
import { User, LoginCredentials, RegisterData, UserRole } from '../../types';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'string') return error;
  if (!isRecord(error)) return fallback;

  const data = error['data'];
  if (isRecord(data)) {
    const apiMessage = data['detail'] ?? data['message'] ?? data['error'];
    if (typeof apiMessage === 'string') return apiMessage;
  }

  return typeof error['message'] === 'string' ? error['message'] : fallback;
};

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue, dispatch }) => {
    try {
      const response = await login(credentials);
      
      // After successful login, fetch permissions
      try {
        const permissionsResponse = await fetchUserPermissions(response.access_token);
        
        // Dispatch the permissions to be stored
        dispatch(setUserPermissions({
          permissions: permissionsResponse.permissions,
          accessible_districts: permissionsResponse.accessible_districts,
          accessible_schools: permissionsResponse.accessible_schools,
        }));
      } catch (permissionsError) {
        console.warn('Failed to fetch permissions:', permissionsError);
        // Don't fail login if permissions fail, just log the warning
      }
      
      return response;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Login failed'));
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (data: RegisterData, { rejectWithValue }) => {
    try {
      const response = await register(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Registration failed'));
    }
  }
);

export const logoutUser = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await logout();
      return true;
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Logout failed'));
    }
  }
);

// Re-fetch the permission list for an existing session (e.g. after redux-persist
// rehydration) so persisted sessions never run on a stale permission set.
export const refreshPermissions = createAsyncThunk(
  'auth/refreshPermissions',
  async (_, { getState, rejectWithValue }) => {
    try {
      // Read the token from the store, not localStorage: right after login,
      // redux-persist may not have flushed the new token to storage yet.
      const { auth } = getState() as { auth: AuthState };
      return await fetchUserPermissions(auth.token || undefined);
    } catch (error: unknown) {
      return rejectWithValue(getErrorMessage(error, 'Failed to load permissions'));
    }
  }
);

export const loadUser = createAsyncThunk(
  'auth/loadUser',
  async (_, { rejectWithValue }) => {
    try {
      const user = await getCurrentUser();
      return { user };
    } catch (error: unknown) {
      // Prioritize API error message, then error.message, then fallback
      return rejectWithValue(getErrorMessage(error, 'Failed to load user'));
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      setMemoryAccessToken(null);
      state.isAuthenticated = false;
      state.error = null;
      state.isLoading = false;
    },
    setUserPermissions: (state, action) => {
      if (state.user) {
        const payload = action.payload as {
          permissions?: { permission: string; scope: string; description: string }[];
          accessible_districts?: number[];
          accessible_schools?: number[];
        };
        state.user.permissions = payload.permissions || [];
        state.user.accessible_districts = payload.accessible_districts || [];
        state.user.accessible_schools = payload.accessible_schools || [];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        // Transform user_info to User type
        const userInfo = action.payload.user_info;
        state.user = {
          id: userInfo.id,
          username: userInfo.username,
          email: userInfo.email,
          full_name: userInfo.full_name,
          role: userInfo.role as UserRole,
          district_id: userInfo.district_id,
          school_id: userInfo.school_id,
          is_superuser: userInfo.is_superuser,
          is_active: true, // Not provided in API response, assume active if login successful
        };
        state.token = action.payload.access_token;
        setMemoryAccessToken(action.payload.access_token);
        state.isAuthenticated = true;
        state.isLoading = false;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.error = action.payload as string;
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        setMemoryAccessToken(null);
      })
      // Register
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, _action) => {
        // Registration successful but user needs to login
        // Don't set authentication state since no token is provided
        // The response contains user data but no access_token
        state.isLoading = false;
        state.error = null;
        // Note: User will be redirected to login page
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.error = action.payload as string;
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      })
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        setMemoryAccessToken(null);
        state.isAuthenticated = false;
        state.error = null;
        state.isLoading = false;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.user = null;
        state.token = null;
        setMemoryAccessToken(null);
        state.isAuthenticated = false;
        state.error = action.payload as string;
        state.isLoading = false;
      })
      // Load user
      .addCase(loadUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(loadUser.fulfilled, (state, action) => {
        // /auth/me does not include permissions; keep the ones we already have
        const existingPermissions = state.user?.permissions;
        const existingDistricts = state.user?.accessible_districts;
        const existingSchools = state.user?.accessible_schools;
        state.user = {
          ...action.payload.user,
          permissions: action.payload.user.permissions ?? existingPermissions,
          accessible_districts: action.payload.user.accessible_districts ?? existingDistricts,
          accessible_schools: action.payload.user.accessible_schools ?? existingSchools,
        };
        state.isAuthenticated = true;
        state.isLoading = false;
      })
      .addCase(loadUser.rejected, (state, action) => {
        state.error = action.payload as string;
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        setMemoryAccessToken(null);
      })
      .addCase(REHYDRATE, (_state, action) => {
        const auth = (action as { payload?: { auth?: AuthState } }).payload?.auth;
        if (auth?.token) {
          setMemoryAccessToken(auth.token);
        }
      })
      // Refresh permissions
      .addCase(refreshPermissions.fulfilled, (state, action) => {
        if (state.user) {
          state.user.permissions = action.payload.permissions || [];
          state.user.accessible_districts = action.payload.accessible_districts || [];
          state.user.accessible_schools = action.payload.accessible_schools || [];
        }
      });
  },
});

export const { clearError, clearAuth, setUserPermissions } = authSlice.actions;
export default authSlice.reducer;